import { FC, useCallback, useMemo, useState } from "react";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";

import { SortableBadge } from "./SortableBadge";
import { StateAction } from "../../../../../helpers/functions/types";
import { FilterRenderData } from "./types";
import { waitSomeTime } from "../../../../../visualGuides/helpers";
import { ATTRIBUTE_GROUP_STYLES } from "../../../../../ui/layout/detailedPages/styles";

const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const handleMove = async (
  setFilterData: StateAction<FilterRenderData[]>,
  dragIndex: number,
  pasteIndex: number,
  setActiveItemIndex: StateAction<number | undefined>,
) => {
  setFilterData((prevData) => {
    const newData = [...prevData];
    const draggedItem = prevData[dragIndex];
    newData.splice(dragIndex, 1);
    newData.splice(pasteIndex, 0, draggedItem);
    return newData;
  });
  setActiveItemIndex(pasteIndex);
};

const handleDrop = async (
  setActiveItemIndex: StateAction<number | undefined>,
) => {
  await waitSomeTime(500);
  setActiveItemIndex(undefined);
};

export const Sortable: FC<{
  filterData: FilterRenderData[];
  setFilterData: StateAction<FilterRenderData[]>;
  disabled?: boolean;
}> = ({ filterData, setFilterData, disabled = true }) => {
  const [activeItemIndex, setActiveItemIndex] = useState<number>();
  const filtersAmount = useMemo(() => filterData.length, [filterData.length]);

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const pasteIndex = Math.min(filtersAmount, hoverIndex);
      if (!disabled)
        handleMove(setFilterData, dragIndex, pasteIndex, setActiveItemIndex);
    },
    [disabled, filterData],
  );

  const keyboardMove = useCallback(
    (targetId: string, arrowKey: string) => {
      const dragIndex = filterData.findIndex(
        ({ targetFilter }) =>
          targetFilter === targetId.split("SortableFilter")[1],
      );
      const pasteIndex =
        arrowKey === "ArrowLeft"
          ? filterData
              .slice(0, dragIndex)
              .findLastIndex(
                ({ activeFilter }) => !!activeFilter?.column.length,
              )
          : filterData
              .slice(dragIndex + 1)
              .findIndex(({ activeFilter }) => !!activeFilter?.column.length) +
            dragIndex +
            1;

      if (pasteIndex >= 0 && !disabled) {
        handleMove(setFilterData, dragIndex, pasteIndex, setActiveItemIndex);
        handleDrop(setActiveItemIndex);
      }
    },
    [disabled, filterData],
  );

  return (
    <div className={ATTRIBUTE_GROUP_STYLES}>
      <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
        {filterData.map((item, index) =>
          item.activeFilter?.column.length ? (
            <SortableBadge
              index={index}
              activeItemIndex={activeItemIndex}
              id={`SortableFilter${item.columnGroupLabel ?? item.targetFilter}`}
              key={index}
              moveCard={moveCard}
              dropCard={() => handleDrop(setActiveItemIndex)}
              keyboardMove={keyboardMove}
              typeId="sortable_component"
              disabled={disabled}
              text={item.activeFilter?.label}
            />
          ) : null,
        )}
      </DndProvider>
    </div>
  );
};
